import React from 'react';
import { TylerState} from '../../utils/TylerState';
import { CarComparisonData } from '../../types/carComparison';
import { calculateResults, calculateMonthlyPayment, getExpectedValueAtEnd, getSellingCosts, calculateFinancePrincipalInterest, calculateLeasePrincipalInterest } from '../../utils/carCalculations';

interface SingleCarResultsProps {
  data: CarComparisonData;
  formatCurrency: (value: number) => string;
}

const SingleCarResults: React.FC<SingleCarResultsProps> = ({ 
  data, 
  formatCurrency 
}) => {
  const financeBreakdown = calculateFinancePrincipalInterest(data);
  const leaseBreakdown = calculateLeasePrincipalInterest(data);

  return (
    <div className="scenario-cards">
      <div className="scenario-card">
        <div className="card-icon">🚗</div>
        <h4>Buy Scenario</h4>
        <p>Cash purchase and ownership costs</p>
        <div className="scenario-total">{formatCurrency(Math.abs(calculateResults(data).buy.finalBalance))}</div>
        <div className="card-details">
          <div className="detail-item">
            <span>Purchase Cost:</span>
            <span>{formatCurrency(data.totalCost + (data.useTaxesPercent ? data.totalCost * data.taxes / 100 : data.taxes) + data.fees)}</span>
          </div>
          <div className="detail-item">
            <span>Maintenance:</span>
            <span>{formatCurrency((data.maintenanceCost + data.otherCarCosts) * data.ownershipTimeframe)}</span>
          </div>
          <div className="detail-item">
            <span>Fuel Costs:</span>
            <span>{formatCurrency((calculateResults(data).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + Math.abs(year.gas), 0))}</span>
          </div>
          <div className="detail-item">
            <span>Insurance:</span>
            <span>{formatCurrency(data.annualInsurance * data.ownershipTimeframe)}</span>
          </div>
          <div className="detail-item">
            <span>Opportunity Cost: Investments:</span>
            <span>{formatCurrency(Math.abs((calculateResults(data).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.investmentReturn, 0)))}</span>
          </div>
          <div className="detail-item">
            <span>Resale Value:</span>
            <span>-{formatCurrency(getExpectedValueAtEnd(data) - getSellingCosts(data))}</span>
          </div>
          <div className="detail-item total">
            <span>Total Cost:</span>
            <span>{formatCurrency(Math.abs(calculateResults(data).buy.finalBalance))}</span>
          </div>
        </div>
      </div>

      {TylerState.car.includeFinanceScenario && (
      <div className="scenario-card">
        <div className="card-icon">💳</div>
        <h4>Finance Scenario</h4>
        <p>Loan payments and financing costs</p>
        <div className="scenario-total">{formatCurrency(Math.abs(calculateResults(data).finance.finalBalance))}</div>
        <div className="card-details">
          <div className="detail-item">
            <span>Monthly Payment:</span>
            <span>{formatCurrency(calculateMonthlyPayment(data))}</span>
          </div>
          <div className="detail-item">
            <span>Total Payments:</span>
            <span>{formatCurrency(financeBreakdown.totalPayments)}</span>
          </div>
          <div className="detail-item breakdown-item">
            <span>Principal:</span>
            <span>{formatCurrency(financeBreakdown.principal)}</span>
          </div>
          <div className="detail-item breakdown-item">
            <span>Interest:</span>
            <span>{formatCurrency(financeBreakdown.interest)}</span>
          </div>
          <div className="detail-item">
            <span>Maintenance:</span>
            <span>{formatCurrency((data.maintenanceCost + data.otherCarCosts) * data.ownershipTimeframe)}</span>
          </div>
          <div className="detail-item">
            <span>Fuel Costs:</span>
            <span>{formatCurrency((calculateResults(data).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + Math.abs(year.gas), 0))}</span>
          </div>
          <div className="detail-item">
            <span>Insurance:</span>
            <span>{formatCurrency(data.annualInsurance * data.ownershipTimeframe)}</span>
          </div>
          <div className="detail-item">
            <span>Opportunity Cost: Investments:</span>
            <span>{formatCurrency(Math.abs((calculateResults(data).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.investmentReturn, 0)))}</span>
          </div>
          <div className="detail-item">
            <span>Resale Value:</span>
            <span>-{formatCurrency(getExpectedValueAtEnd(data) - getSellingCosts(data))}</span>
          </div>
          <div className="detail-item total">
            <span>Total Cost:</span>
            <span>{formatCurrency(Math.abs(calculateResults(data).finance.finalBalance))}</span>
          </div>
        </div>
      </div>
      )}

      {TylerState.car.includeLeaseScenario && (
      <div className="scenario-card">
        <div className="card-icon">📋</div>
        <h4>Lease Scenario</h4>
        <p>Lease payments and contract terms</p>
        <div className="scenario-total">{formatCurrency(Math.abs(calculateResults(data).lease.finalBalance))}</div>
        <div className="card-details">
          <div className="detail-item">
            <span>Monthly Payment:</span>
            <span>{formatCurrency(data.leasePayment)}</span>
          </div>
          <div className="detail-item">
            <span>Total Payments:</span>
            <span>{formatCurrency(leaseBreakdown.totalPayments)}</span>
          </div>
          <div className="detail-item breakdown-item">
            <span>Principal (Depreciation):</span>
            <span>{formatCurrency(leaseBreakdown.principal)}</span>
          </div>
          <div className="detail-item breakdown-item">
            <span>Interest:</span>
            <span>{formatCurrency(leaseBreakdown.interest)}</span>
          </div>
          <div className="detail-item">
            <span>Residual Value Payment:</span>
            <span>{formatCurrency(data.residualValue + data.leaseFees)}</span>
          </div>
          <div className="detail-item">
            <span>Maintenance:</span>
            <span>{formatCurrency((data.maintenanceCost + data.otherCarCosts) * data.ownershipTimeframe)}</span>
          </div>
          <div className="detail-item">
            <span>Fuel Costs:</span>
            <span>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + Math.abs(year.gas), 0))}</span>
          </div>
          <div className="detail-item">
            <span>Insurance:</span>
            <span>{formatCurrency(data.annualInsurance * data.ownershipTimeframe)}</span>
          </div>
          <div className="detail-item">
            <span>Kilometer Overages:</span>
            <span>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + Math.abs(year.overageCost), 0))}</span>
          </div>
          <div className="detail-item">
            <span>Opportunity Cost: Investments:</span>
            <span>{formatCurrency(Math.abs((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.investmentReturn, 0)))}</span>
          </div>
          <div className="detail-item">
            <span>Resale Value:</span>
            <span>-{formatCurrency(getExpectedValueAtEnd(data) - getSellingCosts(data))}</span>
          </div>
          <div className="detail-item total">
            <span>Total Cost:</span>
            <span>{formatCurrency(Math.abs(calculateResults(data).lease.finalBalance))}</span>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default SingleCarResults;